import React from 'react';
import styled from '@emotion/styled';
import ApplicationModel from '../../../../../../shared/models/applications/api/ApplicationModel';
import { silver_two, dark_grey_blue, color_shades_darker } from '../../../../../../shared/constants/colors';
import { EmptyState } from '../../../../../../shared/scenes/organizationSettings/components/MedicalIntegrationConfigSettings';
import { normalizePmsName } from '../../../../../../shared/constants/sparkConstants/dentalPms';
import { DentalFormFieldValues, DentalFormFieldVariables, DentalSandboxWarningType, getDentalSandboxWarnings } from '../dentalSandboxWarnings';
import { PillTone, StatusPill } from './sparkFormPills';

export interface LocationFormValues {
    // Effective values: the location's own, falling back to the Shared Configuration's
    values?: DentalFormFieldValues;
    isLoading?: boolean;
}

interface OfficeConnectionStatusProps {
    locationApps: ApplicationModel[];
    formValues: { [appId: string]: LocationFormValues };
}

interface ConnectionItem {
    label: string;
    variable: string;
    warning: DentalSandboxWarningType;
    // PMS systems this item applies to; all when omitted
    pms?: string[];
}

// Each Spark form field a location needs pointed at its real office, with the warning that flags its sandbox value
const ITEMS: ConnectionItem[] = [
    { label: 'Config ID', variable: DentalFormFieldVariables.medicalIntegrationConfigId, warning: 'sandbox-medical-config' },
    { label: 'Office ID', variable: DentalFormFieldVariables.defaultOfficeId, warning: 'sandbox-office-id' },
    { label: 'Procedure Types', variable: DentalFormFieldVariables.procedureTypes, warning: 'sandbox-procedure-types' },
    { label: 'Cancel Code', variable: DentalFormFieldVariables.cancellationCode, warning: 'sandbox-cancellation-code', pms: ['denticon'] },
    { label: 'Referral Type', variable: DentalFormFieldVariables.referralTypeCode, warning: 'sandbox-referral-code', pms: ['denticon'] },
    { label: 'Default Provider', variable: DentalFormFieldVariables.defaultProviderId, warning: 'default-provider-id', pms: ['cloud-9'] }
];

const PMS_LABELS: { [pms: string]: string } = { 'denticon': 'Denticon', 'cloud-9': 'Cloud 9', 'dentrix-ascend': 'Dentrix Ascend', 'open-dental': 'Open Dental' };

type CellState = 'done' | 'sandbox' | 'missing' | 'n/a';

interface LocationRow {
    app: ApplicationModel;
    pms: string;
    cells: CellState[];
    todo: string[];
    isLoading: boolean;
}

const hasValue = (value?: string | null) => typeof value === 'string' && value.trim() !== '' && value.trim() !== '-1';

const buildRow = (app: ApplicationModel, form?: LocationFormValues): LocationRow => {
    const values = form?.values ?? {};
    const pms = normalizePmsName(values[DentalFormFieldVariables.pmsName] || '');
    const warnings = getDentalSandboxWarnings(values).map(warning => warning.type);
    const cells = ITEMS.map((item): CellState => {
        if (item.pms && !item.pms.includes(pms)) return 'n/a';
        if (warnings.includes(item.warning)) return hasValue(values[item.variable]) ? 'sandbox' : 'missing';
        return hasValue(values[item.variable]) ? 'done' : 'missing';
    });
    const labelsWhere = (state: CellState) => ITEMS.filter((_, i) => cells[i] === state).map(item => item.label);
    const sandbox = labelsWhere('sandbox');
    const missing = labelsWhere('missing');
    const todo = [
        ...(sandbox.length ? [`Sandbox: ${sandbox.join(', ')}`] : []),
        ...(missing.length ? [`Not set: ${missing.join(', ')}`] : [])
    ];
    return { app, pms, cells, todo, isLoading: !!form?.isLoading || !form };
};

const CELL_PILLS: { [state in CellState]: { tone: PillTone; text: string } | null } = {
    'done': { tone: 'done', text: '✓' },
    'sandbox': { tone: 'warning', text: 'Sandbox' },
    'missing': { tone: 'missing', text: 'Not set' },
    'n/a': null
};

// One skimmable table: what's connected to each location's real office and what still needs doing
const OfficeConnectionStatus: React.FC<OfficeConnectionStatusProps> = ({ locationApps, formValues }) => {
    // Only the healthcare locations the parent loaded values for
    const locations = locationApps.filter(app => !app.allowsTemplating && formValues[app.id]);
    if (locations.length === 0) {
        return <EmptyState>No locations yet.</EmptyState>;
    }

    const rows = locations.map(app => buildRow(app, formValues[app.id]))
        .sort((a, b) => (a.app.name || '').localeCompare(b.app.name || ''));
    const isLoading = rows.some(row => row.isLoading);
    const loaded = rows.filter(row => !row.isLoading);
    const connectedCount = loaded.filter(row => row.todo.length === 0).length;
    const needsWork = loaded.filter(row => row.todo.length > 0);
    // Locations with the same to-dos share one line (usually all of them, via the Shared Configuration)
    const groups = needsWork.reduce((result, row) => {
        const key = row.todo.join('|');
        const group = result.find(g => g.key === key);
        if (group) group.names.push(row.app.name); else result.push({ key, todo: row.todo, names: [row.app.name] });
        return result;
    }, [] as { key: string; todo: string[]; names: string[] }[]);
    const groupLabel = (names: string[]) => names.length === locations.length
        ? `All ${names.length} locations`
        : names.length > 3 ? `${names.slice(0, 2).join(', ')} +${names.length - 2} more` : names.join(', ');

    return (
        <Wrapper>
            <Summary>
                <SummaryStat>
                    <StatValue>{connectedCount}/{locations.length}</StatValue>
                    <StatLabel>locations connected</StatLabel>
                </SummaryStat>
                <SummaryStat>
                    <StatValue warning={needsWork.length > 0}>{needsWork.length}</StatValue>
                    <StatLabel>need updates</StatLabel>
                </SummaryStat>
                {isLoading && <Muted>Checking locations...</Muted>}
            </Summary>

            {groups.length > 0 && (
                <ActionList>
                    {groups.map(group => (
                        <ActionItem key={group.key}>
                            <ActionLocation title={group.names.join(', ')}>{groupLabel(group.names)}</ActionLocation>
                            <span>{group.todo.join(' · ')}</span>
                        </ActionItem>
                    ))}
                </ActionList>
            )}

            <TableContainer>
                <Table>
                    <thead>
                        <tr>
                            <Th>Location</Th>
                            <Th>PMS</Th>
                            {ITEMS.map(item => <Th key={item.label}>{item.label}</Th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => (
                            <tr key={row.app.id}>
                                <Td>
                                    <LocationName>{row.app.name}</LocationName>
                                    {!row.app.isLive && <SubText>Not live</SubText>}
                                </Td>
                                <Td>{row.isLoading ? <Muted>...</Muted> : PMS_LABELS[row.pms] ?? (row.pms || <Muted>Not set</Muted>)}</Td>
                                {row.cells.map((state, i) => {
                                    const pill = CELL_PILLS[state];
                                    return (
                                        <Td key={ITEMS[i].label}>
                                            {row.isLoading ? <Muted>...</Muted> : pill ? <StatusPill tone={pill.tone}>{pill.text}</StatusPill> : <Muted>—</Muted>}
                                        </Td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </TableContainer>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    font-family: Muli;
`;

const Summary = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 32px;
`;

const SummaryStat = styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;
`;

const StatValue = styled.span<{ warning?: boolean }>`
    font-size: 24px;
    font-weight: 700;
    color: ${props => props.warning ? '#b7791f' : dark_grey_blue};
`;

const StatLabel = styled.span`
    font-size: 13px;
    color: ${color_shades_darker};
`;

const ActionList = styled.div`
    display: flex;
    flex-direction: column;
    border: 1px solid rgba(214, 158, 46, 0.4);
    border-radius: 8px;
    background-color: rgba(214, 158, 46, 0.06);
`;

const ActionItem = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    padding: 10px 16px;
    font-size: 13px;
    color: #2d3748;

    & + & {
        border-top: 1px solid rgba(214, 158, 46, 0.25);
    }
`;

const ActionLocation = styled.span`
    font-weight: 700;
    color: ${dark_grey_blue};
`;

const TableContainer = styled.div`
    width: 100%;
    overflow-x: auto;
    border: 1px solid ${silver_two};
    border-radius: 8px;
    background-color: #ffffff;
`;

const Table = styled.table`
    width: 100%;
    min-width: 820px;
    border-collapse: collapse;

    tbody tr:last-of-type td {
        border-bottom: none;
    }
`;

const Th = styled.th`
    background-color: #edf2f7;
    border-bottom: 1px solid ${silver_two};
    color: #4a5568;
    font-size: 12px;
    font-weight: 700;
    padding: 10px 12px;
    text-align: left;
    white-space: nowrap;
`;

const Td = styled.td`
    padding: 10px 12px;
    border-bottom: 1px solid ${silver_two};
    font-size: 13px;
    color: #2d3748;
    vertical-align: middle;
    white-space: nowrap;
`;

const LocationName = styled.div`
    font-weight: 600;
    color: ${dark_grey_blue};
`;

const SubText = styled.div`
    font-size: 11px;
    color: ${color_shades_darker};
`;

const Muted = styled.span`
    font-size: 12px;
    color: ${color_shades_darker};
`;

export default OfficeConnectionStatus;
